import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Form, Header, Loader } from 'semantic-ui-react';
import { orderBy } from 'lodash';
import toast from 'react-hot-toast';

import { getCompanyTeam, patchUserActive, postCompanyTeamInvite } from '~/api/admin';
import { Button, Table } from '~/components';
import Modal from '~/components/Modal';
import CompanyRoleTableCell from '~/features/user/settings/CompanySettings/CompanyRoleTableCell';
import DefaultProjectLeadCheckbox from '~/features/user/settings/CompanySettings/DefaultProjectLeadCheckbox';
import RemoveUserModal from '~/modals/RemoveUserModal';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useUserContext } from '~/hooks';
import EditAdminUserModal from '~/features/admin/users/EditAdminUserModal';
import {
  formatUserListDateShort,
  formatUserListLastAccess,
  stopRowClick,
} from '~/features/admin/adminUserListShared';
import CompanyTeamMemberActionsDropdown from './CompanyTeamMemberActionsDropdown';

import '~/features/projectSearch/styles.scss';
import '~/features/bidSearch/styles.scss';
import '~/features/admin/companies/styles.scss';

type SortColumn = 'name' | 'email' | 'companyRoleCode' | 'role' | 'lastAccess';
type SortDir = 'asc' | 'desc';

type CompanyProfileTeamTabProps = {
  company: { type?: string; id?: string };
};

function normalizeTeamMember(member: any) {
  return {
    ...member,
    sortLastAccess:
      member.isTeamInvite && member.createdAt
        ? new Date(member.createdAt).getTime()
        : member.lastAccess
          ? new Date(member.lastAccess).getTime()
          : null,
    displayName: member.name || member.email || '—',
  };
}

export default function CompanyProfileTeamTab({ company }: CompanyProfileTeamTabProps) {
  const { id: companyId } = useParams<{ id: string }>();
  const location = useLocation();
  const { permissions } = usePermissions();
  const { loggedInUser } = useUserContext();
  const queryClient = useQueryClient();
  const [memberSortColumn, setMemberSortColumn] = useState<SortColumn>('lastAccess');
  const [memberSortDir, setMemberSortDir] = useState<SortDir>('desc');
  const [inviteSortColumn, setInviteSortColumn] = useState<SortColumn>('lastAccess');
  const [inviteSortDir, setInviteSortDir] = useState<SortDir>('desc');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [userToRemove, setUserToRemove] = useState<any>();
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  const isInvestorCompany = String(company?.type ?? '')
    .toLowerCase()
    .includes('investor');

  const invalidateTeam = () => {
    queryClient.invalidateQueries(['adminCompanyTeam', companyId]);
  };

  const { data: teamMembers, status } = useQuery({
    queryKey: ['adminCompanyTeam', companyId],
    queryFn: () => getCompanyTeam(companyId!),
    enabled: Boolean(companyId && permissions?.isAdmin),
    refetchOnWindowFocus: false,
  });

  const inviteMutation = useMutation(
    () => postCompanyTeamInvite(companyId!, inviteEmail.trim()),
    {
      onSuccess: () => {
        toast.success('Invitation sent.');
        invalidateTeam();
        setInviteOpen(false);
        setInviteEmail('');
      },
      onError: () => {
        toast.error('Could not send invitation. Try again or check the email address.');
      },
    }
  );

  const { mutate: toggleUserActive } = useMutation(
    ({ userId, nextActive }: { userId: string; nextActive: boolean }) => patchUserActive(userId, nextActive),
    {
      onMutate: ({ userId }) => setTogglingUserId(userId),
      onSuccess: (_, { nextActive }) => {
        invalidateTeam();
        toast.success(nextActive ? 'User activated.' : 'User deactivated.');
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.message ?? err?.response?.data?.errors?.[0]?.message ?? err?.message;
        toast.error(typeof msg === 'string' ? msg : 'Failed to update user.');
      },
      onSettled: () => setTogglingUserId(null),
    }
  );

  const sortKeyForColumn =
    (sortColumn: SortColumn) =>
    (m: any): string | number => {
      switch (sortColumn) {
        case 'lastAccess':
          return m.sortLastAccess ?? -1;
        case 'name':
          return String(m.displayName ?? '').toLowerCase();
        case 'email':
          return String(m.email ?? '').toLowerCase();
        case 'companyRoleCode':
          return String(m.companyRoleCode ?? '').toLowerCase();
        case 'role':
          return String(m.role ?? '').toLowerCase();
        default:
          return '';
      }
    };

  const memberRows = useMemo(() => {
    if (!teamMembers?.length) {
      return [];
    }
    const normalized = teamMembers
      .filter((m: any) => !m.isTeamInvite)
      .map(normalizeTeamMember);
    return orderBy(normalized, [sortKeyForColumn(memberSortColumn)], [memberSortDir]);
  }, [teamMembers, memberSortColumn, memberSortDir]);

  const inviteRows = useMemo(() => {
    if (!teamMembers?.length) {
      return [];
    }
    const normalized = teamMembers.filter((m: any) => m.isTeamInvite).map(normalizeTeamMember);
    return orderBy(normalized, [sortKeyForColumn(inviteSortColumn)], [inviteSortDir]);
  }, [teamMembers, inviteSortColumn, inviteSortDir]);

  const toggleMemberSort = (column: SortColumn) => {
    if (memberSortColumn === column) {
      setMemberSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setMemberSortColumn(column);
      setMemberSortDir('asc');
    }
  };

  const toggleInviteSort = (column: SortColumn) => {
    if (inviteSortColumn === column) {
      setInviteSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setInviteSortColumn(column);
      setInviteSortDir('asc');
    }
  };

  const getMemberSort = (column: SortColumn) => {
    if (memberSortColumn !== column) {
      return undefined;
    }
    return memberSortDir === 'asc' ? 'ascending' : 'descending';
  };

  const getInviteSort = (column: SortColumn) => {
    if (inviteSortColumn !== column) {
      return undefined;
    }
    return inviteSortDir === 'asc' ? 'ascending' : 'descending';
  };

  if (!permissions?.isAdmin) {
    return null;
  }

  if (status === 'loading') {
    return (
      <>
        <Loader active size="big" />
        <p>Loading team…</p>
      </>
    );
  }

  if (status === 'error') {
    return <p>Could not load team members for this company.</p>;
  }

  const headerActions = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1rem',
        gap: '1rem',
        flexWrap: 'wrap',
      }}
    >
      <p style={{ margin: 0, maxWidth: '40rem' }}>
        Manage users and invites for this company. Changes apply the same rules as in the organization’s Settings → Team page.
      </p>
      <Button primary size="medium" onClick={() => setInviteOpen(true)} style={{ marginRight: 0 }}>
        Invite user
      </Button>
    </div>
  );

  const formatMemberLastAccessCell = (member: any) => {
    if (member.lastAccess) {
      return formatUserListLastAccess(member.lastAccess);
    }
    return '—';
  };

  return (
    <div>
      <h2>Team</h2>
      {headerActions}

      {inviteOpen && (
        <Modal closeModal={() => setInviteOpen(false)}>
          <Modal.Title>Invite user</Modal.Title>
          <Modal.Content>
            <p style={{ marginBottom: '1rem' }}>
              They will receive an email with a link to create a Conductor account and join this organization.
            </p>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!inviteEmail.trim()) {
                  return;
                }
                inviteMutation.mutate();
              }}
            >
              <Form.Field>
                <Form.Input
                  type="email"
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(_, d) => setInviteEmail(d.value ?? '')}
                />
              </Form.Field>
            </Form>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              primary
              disabled={!inviteEmail.trim() || inviteMutation.isLoading}
              loading={inviteMutation.isLoading}
              onClick={() => inviteEmail.trim() && inviteMutation.mutate()}
            >
              Send invitation
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <Header as="h3" style={{ marginBottom: '0.75rem' }}>
          Team members
        </Header>
        {!memberRows.length ? (
          <p style={{ margin: 0 }}>No team members yet.</p>
        ) : (
            <Table className="admin-companies-table" striped sortable textAlign="left">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell sorted={getMemberSort('name')} onClick={() => toggleMemberSort('name')}>
                    Name
                  </Table.HeaderCell>
                  <Table.HeaderCell sorted={getMemberSort('email')} onClick={() => toggleMemberSort('email')}>
                    Email
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign="center"
                    sorted={getMemberSort('companyRoleCode')}
                    onClick={() => toggleMemberSort('companyRoleCode')}
                  >
                    User role
                  </Table.HeaderCell>
                  {isInvestorCompany && (
                    <Table.HeaderCell textAlign="center">Default lead</Table.HeaderCell>
                  )}
                  <Table.HeaderCell
                    textAlign="center"
                    sorted={getMemberSort('role')}
                    onClick={() => toggleMemberSort('role')}
                  >
                    User type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign="center"
                    sorted={getMemberSort('lastAccess')}
                    onClick={() => toggleMemberSort('lastAccess')}
                  >
                    Last Access
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'center' }}>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {memberRows.map((member: any) => (
                  <Table.Row
                    key={'user-' + member.id}
                    className={
                      member.active === false ? 'company-profile-team-member-row--deactivated' : undefined
                    }
                  >
                    <Table.Cell>
                      <span className="admin-companies-name-cell">
                        <span>{member.displayName}</span>
                        {member.active === false && !isInvestorCompany && (
                          <span className="admin-companies-deactivated-badge">Deactivated</span>
                        )}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{member.email}</Table.Cell>
                    <CompanyRoleTableCell member={member} onUpdated={invalidateTeam} />
                    {isInvestorCompany &&
                      (member.active === false ? (
                        <Table.Cell textAlign="center">
                          <span className="admin-companies-deactivated-badge">Deactivated</span>
                        </Table.Cell>
                      ) : (
                        <DefaultProjectLeadCheckbox member={member} onUpdated={invalidateTeam} />
                      ))}
                    <Table.Cell>{member.role}</Table.Cell>
                    <Table.Cell>{formatMemberLastAccessCell(member)}</Table.Cell>
                    <CompanyTeamMemberActionsDropdown
                      userId={member.id}
                      email={member.email ?? ''}
                      locationSearch={location.search}
                      isActive={member.active !== false}
                      togglePending={togglingUserId === member.id}
                      currentUserId={loggedInUser?.id}
                      onOpenEdit={() => setEditingUserId(member.id)}
                      onToggleActive={() =>
                        toggleUserActive({
                          userId: member.id,
                          nextActive: member.active === false,
                        })
                      }
                    />
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
        )}
      </div>

      <div>
        <Header as="h3" style={{ marginBottom: '0.75rem' }}>
          Invites
        </Header>
        {!inviteRows.length ? (
          <p style={{ margin: 0 }}>No pending invites.</p>
        ) : (
            <Table className="admin-companies-table" striped sortable textAlign="left">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell sorted={getInviteSort('email')} onClick={() => toggleInviteSort('email')}>
                    Email
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign="center"
                    sorted={getInviteSort('companyRoleCode')}
                    onClick={() => toggleInviteSort('companyRoleCode')}
                  >
                    User role
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign="center"
                    sorted={getInviteSort('role')}
                    onClick={() => toggleInviteSort('role')}
                  >
                    User type
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    textAlign="center"
                    sorted={getInviteSort('lastAccess')}
                    onClick={() => toggleInviteSort('lastAccess')}
                  >
                    Invited
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'center' }}>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {inviteRows.map((member: any) => (
                  <Table.Row key={'invite-' + member.id}>
                    <Table.Cell>{member.email}</Table.Cell>
                    <CompanyRoleTableCell member={member} onUpdated={invalidateTeam} />
                    <Table.Cell>{member.role}</Table.Cell>
                    <Table.Cell>
                      {member.createdAt ? formatUserListDateShort(member.createdAt) : '—'}
                    </Table.Cell>
                    <Table.Cell textAlign="center" onClick={stopRowClick}>
                      <Button
                        negative
                        size="small"
                        content="Revoke invite"
                        onClick={(e) => {
                          stopRowClick(e);
                          setUserToRemove(member);
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
        )}
      </div>

      {editingUserId != null && (
        <EditAdminUserModal
          userId={editingUserId}
          closeModal={() => setEditingUserId(null)}
          onUserSaved={invalidateTeam}
        />
      )}

      {userToRemove && (
        <RemoveUserModal
          user={userToRemove}
          closeModal={() => setUserToRemove(undefined)}
          onRemoved={invalidateTeam}
        />
      )}
    </div>
  );
}
